import type { DocumentsCollection } from 'models';
import { useEffect } from 'react';
import { date as dateUtil } from 'utils/date/date';
import { useTranslations } from 'utils/translations/useTranslations';
import { DocumentCollector } from '../DocumentCollector/DocumentCollector';
import { LRS } from '../LRS/LRS';
import { Online } from '../Online';
import { PaymentInstructions } from '../PaymentInstructions/PaymentInstructions';
import { OfflineRedirection } from './OfflineRedirection/OfflineRedirection';

import './Instructions.scss';

type InstrucionsProps = {
  allCollectionFilesUploaded: boolean;
  allRequiredFilesUploaded: boolean;
  docCollectorFinished: boolean;
  documentsCollection: DocumentsCollection[];
  hasLRSInDocumentCollection: boolean;
  instructionsErrors: any;
  isBoleto: boolean;
  isDocumentsFetchFailure: boolean;
  isLRSPending: boolean;
  isOfflinePayment: boolean;
  isOnline: boolean;
  isStudentPaying: boolean;
  lrsIdVerificationNeeded: boolean;
  onCancel: any;
  onClose: any;
  onPrev: any;
  paymentId: string;
  previousLRSTransactions: any;
  requiresDocuments: boolean;
  setDocCollectorFinished: any;
  shouldDisplayInstructions: boolean;
  studentFullName: any;
  studentID: any;
  uploadLRS: any;
};

const Instructions = ({
  allCollectionFilesUploaded,
  allRequiredFilesUploaded,
  docCollectorFinished,
  documentsCollection = [],
  hasLRSInDocumentCollection,
  instructionsErrors,
  isBoleto = false,
  isDocumentsFetchFailure,
  isLRSPending,
  isOfflinePayment,
  isOnline,
  isStudentPaying,
  lrsIdVerificationNeeded,
  onCancel,
  onClose,
  onPrev,
  paymentId,
  previousLRSTransactions,
  requiresDocuments,
  uploadLRS,
  setDocCollectorFinished,
  shouldDisplayInstructions,
  studentFullName,
  studentID,
}: InstrucionsProps) => {
  const i18n = useTranslations();

  useEffect(() => {
    if (requiresDocuments && !allRequiredFilesUploaded) {
      setDocCollectorFinished(false);
    }
  });

  const renderDocumentCollector = () => {
    if (hasLRSInDocumentCollection) {
      return null;
    }

    if (requiresDocuments && !docCollectorFinished) {
      return <DocumentCollector />;
    }

    return null;
  };

  const shouldShowInstructions = hasLRSInDocumentCollection
    ? true
    : (!requiresDocuments || (requiresDocuments && docCollectorFinished)) &&
      !isDocumentsFetchFailure;

  const shouldShowOfflineRedirectionButton =
    !isBoleto && !isOnline && shouldDisplayInstructions;

  const formattedPreviousTransactions = previousTransactions => {
    if (!previousTransactions) {
      return [];
    }

    return previousTransactions.map(({ date, ...transaction }) => ({
      ...transaction,
      date: dateUtil.american(date),
    }));
  };

  return isLRSPending ? (
    <LRS
      allCollectionFilesUploaded={allCollectionFilesUploaded}
      documentsCollection={documentsCollection}
      onCancel={onPrev}
      onSubmit={uploadLRS}
      initialValues={{
        studentID,
        studentFullName,
      }}
      paymentId={paymentId}
      previousTransactions={formattedPreviousTransactions(
        previousLRSTransactions,
      )}
      idVerificationNeeded={lrsIdVerificationNeeded}
      isStudentPaying={isStudentPaying}
    />
  ) : (
    <div className="Instructions-online" data-testid="instructions">
      {renderDocumentCollector()}

      {shouldShowInstructions ? (
        isOnline ? (
          <Online onCancel={onCancel} />
        ) : (
          <PaymentInstructions
            isOfflinePayment={isOfflinePayment}
            instructionsErrors={instructionsErrors}
          />
        )
      ) : null}

      {shouldShowInstructions && (
        <div className="Instructions-actions">
          {shouldShowOfflineRedirectionButton && !instructionsErrors && (
            <OfflineRedirection onClose={onClose} />
          )}
          {isOfflinePayment && (
            <button onClick={onCancel} className="Instructions-actions--cancel">
              {i18n.t('checkout.cancel.action')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export { Instructions };
